import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { MessageCircle, Wifi, WifiOff, Send, Save, RefreshCw, Eye, EyeOff, CheckCircle2, XCircle, Clock, AlertCircle, ChevronRight, Inbox, Loader2, Copy } from 'lucide-react';
import { fmtDateTime, fmtRelative } from '@/lib/dates';
import { cn } from '@/lib/utils';

const EVENT_GROUP = {
  'agenda_baru': 'Agenda',
  'agenda_disetujui': 'Agenda',
  'agenda_ditolak': 'Agenda',
  'agenda_delegasi': 'Agenda',
  'agenda_reminder_h1': 'Reminder',
  'agenda_reminder_hari_h': 'Reminder',
  'sppd_baru': 'SPPD',
  'sppd_disetujui': 'SPPD',
  'sppd_ditolak': 'SPPD',
  'sppd_aktif': 'SPPD',
};

const EVENT_DESC = {
  'agenda_baru': 'Dikirim ke Kabag saat agenda baru menunggu persetujuan',
  'agenda_disetujui': 'Dikirim ke Operator pembuat saat agenda disetujui',
  'agenda_ditolak': 'Dikirim ke Operator pembuat saat agenda ditolak',
  'agenda_delegasi': 'Dikirim ke Pejabat Delegasi saat ditugaskan',
  'agenda_reminder_h1': 'Reminder otomatis 1 hari sebelum agenda (pukul 17:00 WITA)',
  'agenda_reminder_hari_h': 'Reminder otomatis di hari H (pukul 07:00 WITA)',
  'sppd_baru': 'Dikirim ke Kabag saat SPPD baru menunggu persetujuan',
  'sppd_disetujui': 'Dikirim ke Pejabat saat SPPD disetujui',
  'sppd_ditolak': 'Dikirim ke Operator saat SPPD ditolak',
  'sppd_aktif': 'Dikirim ke Kabag saat SPPD masuk status Sedang Dinas',
};

const PLACEHOLDER_VARIABLES = {
  'agenda_baru': ['nama_target', 'nama_acara', 'tanggal', 'jam', 'lokasi', 'pemohon'],
  'agenda_disetujui': ['nama_target', 'nama_acara', 'tanggal', 'jam', 'lokasi'],
  'agenda_ditolak': ['nama_target', 'nama_acara', 'tanggal', 'alasan'],
  'agenda_delegasi': ['nama_pejabat', 'nama_acara', 'tanggal', 'jam', 'lokasi', 'pakaian', 'disposisi'],
  'agenda_reminder_h1': ['nama_pejabat', 'nama_acara', 'tanggal', 'jam', 'lokasi'],
  'agenda_reminder_hari_h': ['nama_pejabat', 'nama_acara', 'jam', 'lokasi'],
  'sppd_baru': ['nama_target', 'nomor_sppd', 'nama_pejabat', 'tujuan', 'tgl_berangkat', 'tgl_kembali', 'jenis_perjalanan'],
  'sppd_disetujui': ['nama_pejabat', 'nomor_sppd', 'tujuan', 'tgl_berangkat', 'tgl_kembali', 'keperluan'],
  'sppd_ditolak': ['nama_target', 'nomor_sppd', 'tujuan', 'alasan'],
  'sppd_aktif': ['nama_target', 'nama_pejabat', 'tujuan', 'tgl_berangkat', 'tgl_kembali'],
};

export default function WhatsAppSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testDialog, setTestDialog] = useState({ open: false });
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tplEdit, setTplEdit] = useState(null);
  const [reminderRunning, setReminderRunning] = useState(false);

  const load = async () => {
    try {
      const [s, t, st] = await Promise.all([
        api.get('/whatsapp/settings'),
        api.get('/whatsapp/templates'),
        api.get('/whatsapp/stats'),
      ]);
      setSettings(s.data);
      setTemplates(t.data);
      setStats(st.data);
    } catch (e) {
      toast.error('Gagal memuat pengaturan WhatsApp');
    }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: settings.enabled,
        api_token: settings.api_token,
        sender_number: settings.sender_number,
        sender_name: settings.sender_name,
        rate_limit_per_sec: Number(settings.rate_limit_per_sec) || 3,
        retry_max: Number(settings.retry_max) || 3,
        send_delay_sec: Number(settings.send_delay_sec) || 0,
        country_code: settings.country_code || '62',
      };
      await api.patch('/whatsapp/settings', payload);
      toast.success('Pengaturan WhatsApp tersimpan');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post('/whatsapp/test-connection');
      if (data.connected) {
        toast.success(`Terhubung! Status: ${data.message}`);
      } else {
        toast.error(`Tidak terhubung: ${data.message}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal test koneksi');
    } finally {
      setTesting(false);
    }
  };

  const sendTestMessage = async () => {
    if (!testPhone) {
      toast.error('Nomor target wajib diisi');
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post('/whatsapp/send-test', { target_phone: testPhone, message: testMessage || undefined });
      if (data.ok) {
        toast.success('Pesan uji coba berhasil dikirim. Cek log untuk detail.');
      } else {
        toast.error(`Gagal: ${data.reason || 'Unknown'}`);
      }
      setTestDialog({ open: false });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal');
    } finally {
      setSending(false);
    }
  };

  const toggleEvent = async (template, enabled) => {
    try {
      await api.patch(`/whatsapp/templates/${template.event}`, { enabled });
      setTemplates(templates.map((t) => t.event === template.event ? { ...t, enabled } : t));
      toast.success(`Notifikasi ${template.name} ${enabled ? 'diaktifkan' : 'dinonaktifkan'}`);
    } catch (e) {
      toast.error('Gagal');
    }
  };

  const saveTemplate = async () => {
    try {
      await api.patch(`/whatsapp/templates/${tplEdit.event}`, { name: tplEdit.name, body: tplEdit.body });
      toast.success('Template disimpan');
      setTplEdit(null);
      load();
    } catch (e) {
      toast.error('Gagal');
    }
  };

  const resetTemplate = async (event) => {
    if (!window.confirm('Reset template ke default?')) return;
    try {
      await api.post(`/whatsapp/templates/${event}/reset`);
      toast.success('Template direset');
      load();
      setTplEdit(null);
    } catch (e) {
      toast.error('Gagal');
    }
  };

  const runReminder = async (kind) => {
    setReminderRunning(true);
    try {
      const { data } = await api.post(`/whatsapp/reminder/run?kind=${kind}`);
      toast.success(`Reminder ${kind} dijalankan. ${data.kinds_run.join(', ')}`);
      load();
    } catch (e) {
      toast.error('Gagal');
    } finally {
      setReminderRunning(false);
    }
  };

  if (!settings) return <div className="py-16 flex justify-center"><div className="animate-spin w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pengaturan WhatsApp"
        subtitle="Konfigurasi integrasi Fonnte API untuk notifikasi WhatsApp PROAKTIF."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Pengaturan' }, { label: 'WhatsApp' }]}
        action={
          <Link to="/pengaturan/whatsapp/logs"><Button variant="outline"><Inbox className="w-4 h-4 mr-2" />Log WhatsApp</Button></Link>
        }
      />

      {/* Status Banner */}
      <Card className={cn('border-2', settings.enabled ? (settings.connected ? 'border-emerald-300 bg-emerald-50/30' : 'border-amber-300 bg-amber-50/30') : 'border-slate-200 bg-slate-50/30')}>
        <CardContent className="p-5 flex flex-wrap items-center gap-4">
          <div className={cn('w-14 h-14 rounded-2xl flex items-center justify-center', settings.enabled && settings.connected ? 'bg-emerald-100 text-emerald-700' : settings.enabled ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-500')}>
            {settings.enabled ? <Wifi className="w-7 h-7" /> : <WifiOff className="w-7 h-7" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-lg text-slate-900">Layanan WhatsApp</span>
              {settings.enabled ? (
                settings.connected ? (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase">Terhubung</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold uppercase">Tidak Terhubung</span>
                )
              ) : (
                <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 text-xs font-bold uppercase">Nonaktif</span>
              )}
            </div>
            <div className="text-sm text-slate-600 mt-0.5">
              {settings.last_test_at
                ? <>Tes terakhir: {fmtRelative(settings.last_test_at)} — {settings.last_test_message || '-'}</>
                : 'Belum pernah dilakukan test koneksi'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              data-testid="wa-enable-switch"
              checked={settings.enabled}
              onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
            />
            <span className="text-sm font-semibold">Aktifkan</span>
          </div>
        </CardContent>
      </Card>

      {/* Webhook Dua Arah Card */}
      <Card className="border-slate-200 bg-gradient-to-r from-red-50/40 via-amber-50/30 to-white shadow-sm">
        <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-bold text-xs">FITUR PINTAR</span>
              <span className="font-bold text-sm text-slate-900">Webhook Dua Arah (Konfirmasi Kehadiran Pejabat via WA)</span>
            </div>
            <p className="text-xs text-slate-600">
              Salin URL webhook ini ke pengaturan Fonnte Dashboard agar pejabat dapat membalas <b>1 (Hadir)</b> atau <b>2 (Berhalangan)</b> langsung dari chat WhatsApp.
            </p>
            <code className="inline-block mt-1 px-2.5 py-1 rounded bg-white border border-slate-200 text-xs font-mono text-slate-800 select-all">
              https://proaktif.makassarkota.go.id/api/whatsapp/webhook
            </code>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="flex-shrink-0"
            onClick={() => {
              navigator.clipboard.writeText('https://proaktif.makassarkota.go.id/api/whatsapp/webhook');
              toast.success('URL Webhook berhasil disalin ke clipboard');
            }}
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Salin URL
          </Button>
        </CardContent>
      </Card>

      {/* Stats mini */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatBox label="Total Terkirim" value={stats.total} color="slate" icon={MessageCircle} />
          <StatBox label="Sukses" value={stats.success} color="emerald" icon={CheckCircle2} />
          <StatBox label="Gagal" value={stats.failed} color="red" icon={XCircle} />
          <StatBox label="Hari Ini" value={stats.today_total} color="sky" icon={Clock} delta={`${stats.today_success} sukses`} />
          <StatBox label="Success Rate" value={`${stats.success_rate}%`} color="gold" icon={CheckCircle2} />
        </div>
      )}

      <Tabs defaultValue="settings">
        <TabsList className="grid w-full grid-cols-3 max-w-xl">
          <TabsTrigger value="settings" data-testid="tab-wa-settings">Konfigurasi</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-wa-events">Notifikasi & Template</TabsTrigger>
          <TabsTrigger value="reminder" data-testid="tab-wa-reminder">Reminder</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="mt-4 space-y-4">
          <Card className="border-slate-200 shadow-premium">
            <CardHeader><CardTitle className="text-base font-bold">Konfigurasi Fonnte API</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600">
                Dapatkan token Fonnte API di <a href="https://md.fonnte.com" target="_blank" rel="noreferrer" className="text-red-600 hover:underline font-semibold">md.fonnte.com</a> setelah memindai QR WhatsApp Anda.
              </div>
              <div>
                <Label>API Token Fonnte <span className="text-red-600">*</span></Label>
                <div className="relative">
                  <Input
                    data-testid="wa-api-token"
                    type={showToken ? 'text' : 'password'}
                    value={settings.api_token || ''}
                    onChange={(e) => setSettings({ ...settings, api_token: e.target.value })}
                    placeholder="Paste token Fonnte di sini"
                    className="pr-10 font-mono"
                  />
                  <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                    {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Nomor WhatsApp Prokopim (Sender)</Label>
                  <Input data-testid="wa-sender-number" value={settings.sender_number || ''} onChange={(e) => setSettings({ ...settings, sender_number: e.target.value })} placeholder="628xxxxxxxxxx" />
                </div>
                <div>
                  <Label>Nama Pengirim (Display)</Label>
                  <Input value={settings.sender_name || ''} onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })} placeholder="Prokopim Makassar" />
                </div>
                <div>
                  <Label>Country Code</Label>
                  <Input value={settings.country_code || '62'} onChange={(e) => setSettings({ ...settings, country_code: e.target.value })} placeholder="62" />
                </div>
                <div>
                  <Label>Rate Limit (pesan/detik)</Label>
                  <Input type="number" min="1" max="10" value={settings.rate_limit_per_sec || 3} onChange={(e) => setSettings({ ...settings, rate_limit_per_sec: e.target.value })} />
                </div>
                <div>
                  <Label>Max Retry (jika gagal)</Label>
                  <Input type="number" min="1" max="5" value={settings.retry_max || 3} onChange={(e) => setSettings({ ...settings, retry_max: e.target.value })} />
                </div>
                <div>
                  <Label>Delay Pengiriman Fonnte (detik)</Label>
                  <Input type="number" min="0" max="60" value={settings.send_delay_sec || 0} onChange={(e) => setSettings({ ...settings, send_delay_sec: e.target.value })} />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button onClick={saveSettings} disabled={saving} data-testid="wa-save-settings" className="bg-[#DC2626] hover:bg-[#B91C1C]">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Simpan Pengaturan
                </Button>
                <Button variant="outline" onClick={testConnection} disabled={testing} data-testid="wa-test-connection">
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wifi className="w-4 h-4 mr-2" />}
                  Test Connection
                </Button>
                <Button variant="outline" onClick={() => setTestDialog({ open: true })} data-testid="wa-send-test">
                  <Send className="w-4 h-4 mr-2" />Kirim Pesan Uji Coba
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events" className="mt-4 space-y-3">
          {['Agenda', 'Reminder', 'SPPD'].map((group) => (
            <Card key={group} className="border-slate-200 shadow-premium">
              <CardHeader><CardTitle className="text-base font-bold">{group}</CardTitle></CardHeader>
              <CardContent className="divide-y divide-slate-100">
                {templates.filter((t) => EVENT_GROUP[t.event] === group).map((t) => (
                  <div key={t.event} className="py-3 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-slate-900">{t.name}</span>
                        <span className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{t.event}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{EVENT_DESC[t.event]}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button variant="outline" size="sm" onClick={() => setTplEdit(t)} data-testid={`edit-template-${t.event}`}><Eye className="w-3.5 h-3.5 mr-1" />Lihat/Edit Template</Button>
                      <Switch checked={t.enabled !== false} onCheckedChange={(v) => toggleEvent(t, v)} data-testid={`toggle-event-${t.event}`} />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="reminder" className="mt-4 space-y-4">
          <Card className="border-slate-200 shadow-premium">
            <CardHeader><CardTitle className="text-base font-bold">Scheduler Reminder Otomatis</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="font-semibold text-slate-800">⏰ Reminder H-1</div>
                <div className="text-xs text-slate-500 mt-0.5">Berjalan otomatis setiap hari pukul <strong>17:00 WITA</strong>. Mengirim ke pejabat utama / delegasi untuk semua agenda Approved yang dijadwalkan besok.</div>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="font-semibold text-slate-800">🔔 Reminder Hari H</div>
                <div className="text-xs text-slate-500 mt-0.5">Berjalan otomatis setiap hari pukul <strong>07:00 WITA</strong>. Mengirim ke pejabat utama / delegasi untuk semua agenda hari ini.</div>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" onClick={() => runReminder('h1')} disabled={reminderRunning} data-testid="run-reminder-h1">
                  {reminderRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Jalankan Reminder H-1 Sekarang
                </Button>
                <Button variant="outline" onClick={() => runReminder('hari_h')} disabled={reminderRunning}>
                  <RefreshCw className="w-4 h-4 mr-2" />Jalankan Reminder Hari H Sekarang
                </Button>
              </div>
              <div className="text-xs text-slate-500 italic">Catatan: pengiriman manual juga akan menandai agenda sebagai sudah dikirim reminder agar tidak duplikat saat schedule otomatis berjalan.</div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Test Send Dialog */}
      <Dialog open={testDialog.open} onOpenChange={(o) => setTestDialog({ open: o })}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Kirim Pesan Uji Coba</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nomor Tujuan (format: 628xxxx atau +628xxxx)</Label>
              <Input data-testid="wa-test-phone" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="628xxxxxxxxxx" />
            </div>
            <div>
              <Label>Pesan (opsional - kosongkan untuk pesan default)</Label>
              <Textarea rows={5} value={testMessage} onChange={(e) => setTestMessage(e.target.value)} placeholder="Biarkan kosong untuk pakai pesan default uji coba" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialog({ open: false })}>Batal</Button>
            <Button onClick={sendTestMessage} disabled={sending} data-testid="wa-send-test-submit" className="bg-[#DC2626] hover:bg-[#B91C1C]">
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Mengirim…</> : <><Send className="w-4 h-4 mr-2" />Kirim</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={!!tplEdit} onOpenChange={(o) => !o && setTplEdit(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{tplEdit?.name}</DialogTitle></DialogHeader>
          {tplEdit && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500">{EVENT_DESC[tplEdit.event]}</div>
              <div>
                <Label>Variabel Tersedia (gunakan dalam kurung kurawal)</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {(PLACEHOLDER_VARIABLES[tplEdit.event] || []).map((v) => (
                    <code key={v} className="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 cursor-pointer hover:bg-amber-100"
                      onClick={() => setTplEdit({ ...tplEdit, body: (tplEdit.body || '') + `{${v}}` })}
                    >{`{${v}}`}</code>
                  ))}
                </div>
              </div>
              <div>
                <Label>Judul / Nama Internal</Label>
                <Input value={tplEdit.name || ''} onChange={(e) => setTplEdit({ ...tplEdit, name: e.target.value })} />
              </div>
              <div>
                <Label>Isi Pesan WhatsApp</Label>
                <Textarea data-testid="template-body" rows={14} value={tplEdit.body || ''} onChange={(e) => setTplEdit({ ...tplEdit, body: e.target.value })} className="font-mono text-sm" />
                <p className="text-[11px] text-slate-500 mt-1">Gunakan <code>*tebal*</code> untuk bold dan <code>_miring_</code> untuk italic (format WhatsApp).</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => resetTemplate(tplEdit.event)} className="text-amber-700">
              <RefreshCw className="w-4 h-4 mr-2" />Reset ke Default
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setTplEdit(null)}>Batal</Button>
            <Button onClick={saveTemplate} data-testid="save-template" className="bg-[#DC2626] hover:bg-[#B91C1C]"><Save className="w-4 h-4 mr-2" />Simpan Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, color, delta }) {
  const colors = {
    slate: 'bg-slate-50 text-slate-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    red: 'bg-red-50 text-red-600',
    sky: 'bg-sky-50 text-sky-600',
    gold: 'bg-amber-50 text-amber-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-premium">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center', colors[color])}>
          <Icon className="w-3.5 h-3.5" />
        </div>
      </div>
      <div className="text-2xl font-extrabold mt-1 tabular text-slate-900">{value}</div>
      {delta && <div className="text-[10px] text-slate-500">{delta}</div>}
    </div>
  );
}
